import Button from '@rio-cloud/rio-uikit/Button';
import Divider from '@rio-cloud/rio-uikit/Divider';
import Tag from '@rio-cloud/rio-uikit/Tag';
import useAutoAnimate from '@rio-cloud/rio-uikit/useAutoAnimate';
import { useEffect, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import type { MaterialType } from '#domain/Material.ts';

import type { OrderDialogState } from '../../orderCreate/ui/OrderDialog.tsx';
import { emptyMaterial, initialMaterial } from '../model/FormData.ts';
import type { MaterialFormData } from '../model/OrderFormValidation.ts';
import { FreightForm } from './FreightForm.tsx';
import { MaterialTypeChangeConfirmationDialog } from './MaterialTypeChangeConfirmationDialog.tsx';

type AddFreightProps = {
    state: OrderDialogState;
    namePrefix?: `orders.${number}`;
};

type PendingFirstMaterialChange = {
    packageType: string;
    type: MaterialType;
};

export const AddFreight = ({ state, namePrefix }: AddFreightProps) => {
    const name = namePrefix ? (`${namePrefix}.materials` as const) : ('materials' as const);
    const { control, getValues, setValue } = useFormContext();
    const { fields, append, remove } = useFieldArray({
        control,
        name: name,
    });
    const [pendingFirstMaterialChange, setPendingFirstMaterialChange] = useState<PendingFirstMaterialChange>();

    const [parent] = useAutoAnimate();

    const addFreightItem = () => {
        const materials: MaterialFormData[] = getValues(name);
        const firstMaterial = materials[0];

        if (!firstMaterial) {
            append(initialMaterial);
        } else {
            append(emptyMaterial(firstMaterial));
        }
    };

    const removeMaterialItem = (index: number) => {
        remove(index);
    };

    const handleFirstMaterialPackageTypeSelect = (packageType: string, type: MaterialType): boolean => {
        const currentFirstMaterialType = getValues(`${name}.0.type`);
        const materialCount = getValues(`${name}`).length;
        const isTypeChange = currentFirstMaterialType && currentFirstMaterialType !== type;

        if (isTypeChange && materialCount > 1) {
            setPendingFirstMaterialChange({ packageType, type });
            return false;
        }
        return true;
    };

    const closeConfirmationDialog = () => {
        setPendingFirstMaterialChange(undefined);
    };

    const confirmFirstMaterialTypeChange = () => {
        if (!pendingFirstMaterialChange) {
            return;
        }

        setValue(`${name}.0.packageType`, pendingFirstMaterialChange.packageType, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
        });
        setValue(`${name}.0.type`, pendingFirstMaterialChange.type, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
        });

        const materialCount = getValues(name).length;
        if (materialCount > 1) {
            const removableIndexes = Array.from({ length: materialCount - 1 }, (_, index) => index + 1);
            remove(removableIndexes);
        }
        closeConfirmationDialog();
    };

    useEffect(() => {
        if (fields.length === 0 && state === 'createOrder') {
            addFreightItem();
        }
    }, []);
    return (
        <div ref={parent}>
            {fields.map((field, i) => (
                <div className='padding-top-10' key={field.id}>
                    <div className='display-flex justify-content-end padding-right-3 padding-bottom-2'>
                        <Button
                            variant='link-inline'
                            bsStyle='muted'
                            className='text-size-12 padding-left-5'
                            bsSize='xs'
                            autoTrackingKey='ordercom.orders.create-dialog.step2.remove-freight-item'
                            onClick={() => removeMaterialItem(i)}
                            data-testid={`remove-freight-item-${i}`}
                        >
                            <FormattedMessage
                                id={'intl-msg:ordercom-cloud-frontend.orders.create-dialog.step2.remove'}
                            />
                            <span className='uikit-icon icon-trash text-color-gray' />
                        </Button>
                    </div>
                    <FreightForm
                        index={i}
                        name={name}
                        onPackageTypeSelect={i === 0 ? handleFirstMaterialPackageTypeSelect : undefined}
                    />
                </div>
            ))}

            <Divider spacing={20}>
                <Tag clickable iconName='plus' onClick={addFreightItem}>
                    <FormattedMessage
                        id={'intl-msg:ordercom-cloud-frontend.orders.create-dialog.step2.add-more-load'}
                    />
                </Tag>
            </Divider>
            <MaterialTypeChangeConfirmationDialog
                show={!!pendingFirstMaterialChange}
                onCancel={closeConfirmationDialog}
                onConfirm={confirmFirstMaterialTypeChange}
            />
        </div>
    );
};
